import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { ICountryDTO } from '@common-frontend-submodule/interfaces/country/icountry.interface';
import { ICountryCreationDTO } from '@common-frontend-submodule/interfaces/country/icountry-creation.interface';
import { ICountryUpdateDTO } from '@common-frontend-submodule/interfaces/country/icountry-update.interface';
import { IGetCountrysRequestDTO } from '@common-frontend-submodule/interfaces/country/iget-countrys-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { CountryRoutes } from '@common-frontend-submodule/enums/routes/country-routes.enum';

const apiClient = ApiClient.getInstance()

interface CountryContextProps {
  getById: (countryId: string) => Promise<IApiResponseGenericDTO<ICountryDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<ICountryDTO[]>>;
  filter: (model: IGetCountrysRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICountryDTO>>>;
  create: (model: ICountryCreationDTO) => Promise<IApiResponseGenericDTO<ICountryDTO>>;
  update: (countryId: string, model: ICountryUpdateDTO) => Promise<IApiResponseGenericDTO<ICountryDTO>>;
  delete: (countryId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const CountryContext = createContext<CountryContextProps | undefined>(undefined);

export const CountryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (countryId: string): Promise<IApiResponseGenericDTO<ICountryDTO>> => {
    return await apiClient.getRequest<ICountryDTO>(`${CountryRoutes.BasePrefix}/${countryId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<ICountryDTO[]>> => {
    return await apiClient.getRequest<ICountryDTO[]>(`${CountryRoutes.BasePrefix}/${CountryRoutes.All}`);
  };

  const filter = async (model: IGetCountrysRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICountryDTO>>> => {
    return await apiClient.postRequest<IGetCountrysRequestDTO, IPaginatedResultDTO<ICountryDTO>>(
      `${CountryRoutes.BasePrefix}/${CountryRoutes.List}`,
      model
    );
  };

  const create = async (model: ICountryCreationDTO): Promise<IApiResponseGenericDTO<ICountryDTO>> => {
    return await apiClient.postRequest<ICountryCreationDTO, ICountryDTO>(CountryRoutes.BasePrefix, model);
  };

  const update = async (countryId: string, model: ICountryUpdateDTO): Promise<IApiResponseGenericDTO<ICountryDTO>> => {
    return await apiClient.putRequest<ICountryUpdateDTO, ICountryDTO>(`${CountryRoutes.BasePrefix}/${countryId}`, model);
  };

  const deleteCountry = async (countryId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${CountryRoutes.BasePrefix}/${countryId}`);
  };

  return (
    <CountryContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteCountry }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountryStore = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountryStore must be used within a CountryProvider');
  }
  return context;
};
